import { useMemo, useState } from "react";
import { useGetAllPrePlanting } from "../../api/scouting";
import { useNavigate } from "react-router-dom";
import { AppButton } from "../../components/ui/AppButton";
import { PrePlanting } from "../../types/pre-planting";
import { Icons } from "../../assets/images/icons";
import { useTablePrint } from "../../hooks/useTablePrint";
import { ExportButton } from "../../components/ui/ExportButton";
import { DataTable } from "../../components/ui/data-table";
import { formatDateToISO } from "../../util/date-formatter";
import { Input } from "../../components/ui/input";

const PrePlantingPage = () => {
  const navigate = useNavigate();

  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: prePlantingData, isLoading } = useGetAllPrePlanting({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform API data */
  const transformedPrePlantingData = useMemo((): PrePlanting[] => {
    if (!prePlantingData?.data?.content) return [];

    return prePlantingData.data.content.map((item: any) => ({
      id: item.id,
      date: item.date,
      landSize: item.landSize || 0,
      preparationRating: item.preparationRating,
      createdAt: item.createdAt,
      createdBy: item.createdBy,
    }));
  }, [prePlantingData]);

  const handleRowClick = (record: any) => {
    navigate(`/scouting/pre-planting/${record.id}`);
  };

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  /** Define columns for the DataTable */
  const prePlantingColumns = [
    {
      key: "date",
      title: "Date",
      dataIndex: "date",
      align: "left" as const,
    },
    {
      key: "landSize",
      title: "Land Size (ha)",
      dataIndex: "landSize",
      align: "left" as const,
    },
    {
      key: "preparationRating",
      title: "Preparation Rating",
      dataIndex: "preparationRating",
      align: "left" as const,
    },
    {
      key: "createdAt",
      title: "Created At",
      dataIndex: "createdAt",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "createdBy",
      title: "Created By",
      dataIndex: "createdBy",
      align: "left" as const,
    },
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search here..."
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(
                prePlantingColumns,
                transformedPrePlantingData,
                "Pre-Planting Report"
              )
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={prePlantingColumns}
            data={transformedPrePlantingData}
            fileName="pre-planting"
            title="Pre-Planting Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: prePlantingData?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      <div className=" hidden md:block">
        <DataTable<PrePlanting>
          data={transformedPrePlantingData}
          columns={prePlantingColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: prePlantingData?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No item found"
          rowKey="id"
        />
      </div>
    </>
  );
};

export default PrePlantingPage;
